import React, { useState, useEffect } from 'react';
import { 
  Lightbulb, MessageSquarePlus, Send, X, Check, Clock, XCircle, 
  AlertCircle, ChevronDown, ChevronUp, Reply, Eye, EyeOff
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  in_progress: { label: 'In lavorazione', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  resolved: { label: 'Risolto', color: 'bg-green-100 text-green-700', icon: Check },
  rejected: { label: 'Respinto', color: 'bg-red-100 text-red-700', icon: XCircle }
};

function Suggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    titolo: '',
    tipo: 'suggerimento',
    descrizione: '',
    anonimo: false
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Response form state
  const [responseData, setResponseData] = useState({
    status: 'pending',
    risposta: ''
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchSuggestions();
    fetchStats();
  }, [filter]);

  const fetchCurrentUser = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setCurrentUser(await res.json());
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      
      const res = await fetch(`${API_URL}/api/suggestions${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setSuggestions(await res.json());
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/suggestions/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (err) {
      // Non-admin users won't have access to stats
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titolo.trim() || !formData.descrizione.trim()) return;

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        titolo: formData.titolo,
        tipo: formData.tipo,
        descrizione: formData.descrizione,
        anonimo: formData.anonimo
      });
      
      const res = await fetch(`${API_URL}/api/suggestions?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setShowModal(false);
        setFormData({ titolo: '', tipo: 'suggerimento', descrizione: '', anonimo: false });
        fetchSuggestions();
        fetchStats();
      }
    } catch (err) {
      console.error('Error creating suggestion:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResponse = async (suggestionId) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        status: responseData.status,
        risposta: responseData.risposta
      });
      
      const res = await fetch(`${API_URL}/api/suggestions/${suggestionId}?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setShowResponseModal(null);
        setResponseData({ status: 'pending', risposta: '' });
        fetchSuggestions();
        fetchStats();
      }
    } catch (err) {
      console.error('Error responding to suggestion:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (suggestionId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo suggerimento?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/suggestions/${suggestionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSuggestions();
      fetchStats();
    } catch (err) {
      console.error('Error deleting suggestion:', err);
    }
  };

  const isAdminOrHSE = currentUser?.role === 'admin' || currentUser?.role === 'hse_manager';

  return (
    <div className="space-y-6" data-testid="suggestions-page">
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Lightbulb className="text-yellow-500" size={28} />
              Cassetto Suggerimenti
            </h1>
            <p className="text-muted-foreground">
              {isAdminOrHSE 
                ? 'Visualizza e rispondi ai suggerimenti dei dipendenti' 
                : 'Invia suggerimenti e richieste alla direzione'}
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary"
            data-testid="new-suggestion-button"
          >
            <MessageSquarePlus size={18} />
            Nuovo Suggerimento
          </button>
        </div>

        {/* Stats for Admin/HSE */}
        {isAdminOrHSE && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="p-4 bg-muted/50 rounded-lg text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Totali</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-yellow-700">In Attesa</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.in_progress}</p>
              <p className="text-sm text-blue-700">In Lavorazione</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
              <p className="text-sm text-green-700">Risolti</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              <p className="text-sm text-red-700">Respinti</p>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'pending', 'in_progress', 'resolved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-primary text-white'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {status === 'all' ? 'Tutti' : STATUS_CONFIG[status].label}
            </button>
          ))}
        </div>

        {/* Suggestions List */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Lightbulb size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nessun suggerimento trovato</p>
            <button
              onClick={() => setShowModal(true)}
              className="btn btn-primary mt-4"
            >
              Invia il primo suggerimento
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {suggestions.map((sug) => {
              const StatusIcon = STATUS_CONFIG[sug.status]?.icon || Clock;
              const isExpanded = expandedId === sug._id;
              
              return (
                <div
                  key={sug._id}
                  className="border rounded-lg overflow-hidden bg-white"
                  data-testid="suggestion-card"
                >
                  {/* Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : sug._id)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Author Photo */}
                      {sug.anonimo ? (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <EyeOff size={18} className="text-gray-500" />
                        </div>
                      ) : sug.author_foto ? (
                        <img
                          src={`${API_URL}${sug.author_foto}`}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold flex-shrink-0">
                          {sug.author_nome?.charAt(0)}
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            sug.tipo === 'suggerimento' 
                              ? 'bg-yellow-100 text-yellow-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {sug.tipo === 'suggerimento' ? 'Suggerimento' : 'Richiesta'}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${STATUS_CONFIG[sug.status]?.color}`}>
                            <StatusIcon size={12} />
                            {STATUS_CONFIG[sug.status]?.label}
                          </span>
                        </div>
                        <h3 className="font-semibold mt-1">{sug.titolo}</h3>
                        <p className="text-sm text-muted-foreground">
                          {sug.author_nome} • {formatDistanceToNow(new Date(sug.created_at), { addSuffix: true, locale: it })}
                        </p>
                      </div>
                      
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t">
                      <div className="pt-4">
                        <p className="whitespace-pre-wrap">{sug.descrizione}</p>
                        
                        {/* Response */}
                        {sug.risposta && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm font-semibold text-blue-800 mb-1 flex items-center gap-2">
                              <Reply size={14} />
                              Risposta da {sug.risposta_da}
                            </p>
                            <p className="text-sm text-blue-700">{sug.risposta}</p>
                            <p className="text-xs text-blue-500 mt-2">
                              {sug.risposta_at && format(new Date(sug.risposta_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                            </p>
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex gap-2 mt-4">
                          {isAdminOrHSE && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setResponseData({ status: sug.status, risposta: sug.risposta || '' });
                                setShowResponseModal(sug);
                              }}
                              className="btn btn-primary btn-sm"
                            >
                              <Reply size={14} />
                              Rispondi
                            </button>
                          )}
                          {(sug.author_id === currentUser?._id || currentUser?.role === 'admin') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(sug._id);
                              }}
                              className="btn btn-secondary btn-sm text-red-600 hover:bg-red-50"
                            >
                              Elimina
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Suggestion Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg">Nuovo Suggerimento</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-muted rounded">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Type */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, tipo: 'suggerimento' })}
                  className={`flex-1 py-3 rounded-lg flex items-center justify-center gap-2 ${
                    formData.tipo === 'suggerimento' ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300' : 'bg-muted'
                  }`}
                >
                  <Lightbulb size={18} />
                  Suggerimento
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, tipo: 'richiesta' })}
                  className={`flex-1 py-3 rounded-lg flex items-center justify-center gap-2 ${
                    formData.tipo === 'richiesta' ? 'bg-purple-100 text-purple-700 border-2 border-purple-300' : 'bg-muted'
                  }`}
                >
                  <MessageSquarePlus size={18} />
                  Richiesta
                </button>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-1">Titolo *</label>
                <input
                  type="text"
                  value={formData.titolo}
                  onChange={(e) => setFormData({ ...formData, titolo: e.target.value })}
                  placeholder="Inserisci un titolo breve"
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione *</label>
                <textarea
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  placeholder="Descrivi in dettaglio il tuo suggerimento o richiesta..."
                  className="w-full p-3 border rounded-lg min-h-[120px] focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  required
                />
              </div>

              {/* Anonymous */}
              <label className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.anonimo}
                  onChange={(e) => setFormData({ ...formData, anonimo: e.target.checked })}
                  className="w-5 h-5 rounded"
                />
                <div>
                  <p className="font-medium flex items-center gap-2">
                    <EyeOff size={16} />
                    Invia in modo anonimo
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Il tuo nome non sarà visibile
                  </p>
                </div>
              </label>
            </form>

            <div className="p-4 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !formData.titolo.trim() || !formData.descrizione.trim()}
                className="btn btn-primary"
              >
                {submitting ? 'Invio...' : 'Invia'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Response Modal (Admin/HSE only) */}
      {showResponseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg">Rispondi al Suggerimento</h3>
              <button onClick={() => setShowResponseModal(null)} className="p-1 hover:bg-muted rounded">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Original Suggestion */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-semibold">{showResponseModal.titolo}</p>
                <p className="text-sm text-muted-foreground mt-1">{showResponseModal.descrizione}</p>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium mb-1">Stato</label>
                <select
                  value={responseData.status}
                  onChange={(e) => setResponseData({ ...responseData, status: e.target.value })}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="pending">In attesa</option>
                  <option value="in_progress">In lavorazione</option>
                  <option value="resolved">Risolto</option>
                  <option value="rejected">Respinto</option>
                </select>
              </div>

              {/* Response */}
              <div>
                <label className="block text-sm font-medium mb-1">Risposta</label>
                <textarea
                  value={responseData.risposta}
                  onChange={(e) => setResponseData({ ...responseData, risposta: e.target.value })}
                  placeholder="Scrivi una risposta..."
                  className="w-full p-3 border rounded-lg min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowResponseModal(null)}
                className="btn btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={() => handleResponse(showResponseModal._id)}
                disabled={submitting}
                className="btn btn-primary"
              >
                {submitting ? 'Salvataggio...' : 'Salva Risposta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Suggestions;
